import type { IssueSeverity } from '@/shared/constants/issue-status'

import { isActive } from './issues'
import { RESOLUTION_TARGETS, resolutionStatus, type ResolutionStatus, type ResolutionTargets } from './resolution-targets'

import type { GisIssue, GisSite } from '../api/types'

export interface WorkloadRow {
  id: string
  title: string
  severity: IssueSeverity
  status: string
  siteName: string
  mandal: string
  age: number
  flag: ResolutionStatus
}

export interface DepartmentWorkload {
  department: string
  unresolved: number
  critical: number
  overdue: number
  avgAge: number
  oldest: number
  siteCount: number
  rows: WorkloadRow[]
}

const ageFrom = (createdAt: string, now: number): number =>
  Math.max(0, Math.floor((now - new Date(createdAt).getTime()) / 86_400_000))

/** Group the issues feed by department and compute the HOD-drawer stats + rows. Active (non-closed) only. */
export function buildDepartmentWorkload(
  issues: GisIssue[],
  sites: GisSite[],
  department: string,
  targets: ResolutionTargets = RESOLUTION_TARGETS,
  now: number = Date.now(),
): DepartmentWorkload {
  const mandalOf = new Map(sites.map((s) => [s.id, s.mandal]))
  const active = issues.filter((i) => i.department === department && isActive(i))
  const rows: WorkloadRow[] = active.map((i) => {
    const age = ageFrom(i.createdAt, now)
    return {
      id: i.id,
      title: i.title,
      severity: i.severity,
      status: i.status,
      siteName: i.siteName ?? '—',
      mandal: i.siteId !== null ? (mandalOf.get(i.siteId) ?? '') : '',
      age,
      flag: resolutionStatus(i.severity, age, targets),
    }
  })
  const critical = rows.filter((r) => r.severity === 'critical').length
  const overdue = rows.filter((r) => r.flag === 'overdue').length
  const ages = rows.map((r) => r.age)
  const oldest = ages.length > 0 ? Math.max(...ages) : 0
  const avgAge = ages.length > 0 ? Math.round(ages.reduce((a, b) => a + b, 0) / ages.length) : 0
  const siteCount = new Set(active.map((i) => i.siteId).filter((x): x is string => x !== null)).size
  return { department, unresolved: rows.length, critical, overdue, avgAge, oldest, siteCount, rows }
}

export type WorkloadSort = 'aging' | 'severity' | 'status'
const SEV_RANK: Record<IssueSeverity, number> = { critical: 0, non_critical: 1 }
const FLAG_RANK: Record<ResolutionStatus, number> = { overdue: 0, 'due-soon': 1, 'on-track': 2 }

/** Non-mutating sort of workload rows by the selected mode. */
export function sortWorkloadRows(rows: WorkloadRow[], mode: WorkloadSort): WorkloadRow[] {
  const out = [...rows]
  if (mode === 'severity') {
    out.sort((a, b) => SEV_RANK[a.severity] - SEV_RANK[b.severity] || b.age - a.age)
  } else if (mode === 'status') {
    out.sort((a, b) => FLAG_RANK[a.flag] - FLAG_RANK[b.flag] || b.age - a.age)
  } else {
    out.sort((a, b) => b.age - a.age)
  }
  return out
}
